/**
 * Global admin status manager - singleton pattern
 * Updated by AdminStatusProvider React context
 */
class AdminStatusManager {
  private static instance: AdminStatusManager;
  private isAdmin: boolean = false;

  private constructor() {}

  static getInstance(): AdminStatusManager {
    if (!AdminStatusManager.instance) {
      AdminStatusManager.instance = new AdminStatusManager();
    }
    return AdminStatusManager.instance;
  }

  setAdminStatus(isAdmin: boolean): void {
    this.isAdmin = isAdmin;
  }

  getAdminStatus(): boolean {
    return this.isAdmin;
  }
}

/**
 * Logger class that respects dev mode and admin status
 */
export class Logger {
  private namespace?: string;
  private isDev: boolean;

  constructor(namespace?: string) {
    this.namespace = namespace;
    this.isDev = import.meta.env.DEV;
  }

  private shouldLog(): boolean {
    // Always log if in dev mode
    if (this.isDev) {
      return true;
    }

    // In production, only log if user is admin
    return AdminStatusManager.getInstance().getAdminStatus();
  }

  private formatMessage(...args: any[]): any[] {
    if (this.namespace) {
      return [`[${this.namespace}]`, ...args];
    }
    return args;
  }

  getNamespace() {
    return this.namespace;
  }

  setNamespace(namespace: string) {
    this.namespace = namespace;
  }

  debug(...args: any[]): void {
    if (this.shouldLog()) {
      console.debug(...this.formatMessage(...args));
    }
  }

  log(...args: any[]): void {
    if (this.shouldLog()) {
      console.log(...this.formatMessage(...args));
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog()) {
      console.info(...this.formatMessage(...args));
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog()) {
      console.warn(...this.formatMessage(...args));
    }
  }

  error(...args: any[]): void {
    // Always log errors, even in production
    console.error(...this.formatMessage(...args));
  }
}

/**
 * Factory function to create a logger instance
 * @param namespace Optional namespace/prefix for log messages
 */
export function createLogger(namespace?: string): Logger {
  return new Logger(namespace);
}

/**
 * Update the global admin status (called by AdminStatusProvider)
 */
export function setGlobalAdminStatus(isAdmin: boolean): void {
  AdminStatusManager.getInstance().setAdminStatus(isAdmin);
}
